import React, { useState, useEffect, useMemo } from 'react';
import { Bell, BarChart3, ShoppingCart, Package } from 'lucide-react';
import { fetchOrders } from '../api';

const Dashboard = ({ onNotificationClick, products = [] }) => {
  const [activeWidget, setActiveWidget] = useState('overview');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetchOrders().then(rawOrders => {
      if (mounted) {
        const formattedOrders = (rawOrders || []).map(o => {
          const dateObj = new Date(o.order_date || o.created_at);
          return {
            ...o,
            id: `ORD${String(o.id).padStart(3, '0')}`,
            rawId: o.id,
            customer: o.customer_name || 'Guest User',
            total: parseFloat(o.total_amount) || 0,
            status: o.status || 'pending',
            date: dateObj.toLocaleDateString('en-GB').replace(/\//g, '.'),
            monthKey: `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`
          };
        });
        setOrders(formattedOrders);
        setLoading(false);
      }
    }).catch(err => {
      console.error(err);
      if (mounted) setLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  // Compute live stats
  const stats = useMemo(() => {
    const totalOrders = orders.length;
    let totalRevenue = 0;
    const customerSet = new Set();
    
    // Reverse iterating to mock "recent" easily, assuming sequential IDs or fetch order
    orders.forEach(o => {
      totalRevenue += o.total || 0;
      if (o.customer || o.phone) customerSet.add(o.phone || o.customer);
    });

    const lowStock = products.filter(p => p.stock < 20 && p.stock > 0).length;
    const expiringProducts = products.filter(p => p.expiryDate && new Date(p.expiryDate) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)).length;

    return { totalOrders, totalRevenue, activeCustomers: customerSet.size, lowStock, expiringProducts };
  }, [orders, products]);

  // Quick Analytics: group revenue by last 5 months
  const monthlyData = useMemo(() => {
    const months = {};
    for (let i = 4; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months[key] = { label: d.toLocaleString('default', { month: 'short' }), total: 0 };
    }
    
    orders.forEach(o => {
      if (!o.date) return;
      // o.date is usually dd.mm.yyyy or yyyy-mm-dd
      let yyyy, mm;
      if (o.date.includes('.')) {
        const [d, m, y] = o.date.split('.');
        yyyy = y; mm = m;
      } else {
        const d = new Date(o.date);
        yyyy = d.getFullYear(); mm = String(d.getMonth() + 1).padStart(2, '0');
      }
      const key = `${yyyy}-${mm}`;
      if (months[key]) months[key].total += o.total || 0;
    });
    
    return Object.values(months);
  }, [orders]);

  const recentOrders = [...orders].reverse().slice(0, 12);
  const criticalProducts = products.filter(p => p.stock < 20 || (p.expiryDate && new Date(p.expiryDate) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)));
  const recentProducts = [...products].reverse().slice(0, 8);

  const notifications = [
    ...(stats.lowStock > 0 ? [{ id: 's1', message: `${stats.lowStock} products have low stock`, type: 'stock' }] : []),
    ...(stats.expiringProducts > 0 ? [{ id: 'e1', message: `${stats.expiringProducts} products expiring soon`, type: 'stock' }] : [])
  ];

  const widgets = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'orders', label: 'Recent Orders', icon: ShoppingCart },
    { id: 'products', label: 'Product Status', icon: Package },
    { id: 'analytics', label: 'Quick Analytics', icon: BarChart3 }
  ];

  return (
    <div className="ui-page animate-fade-in">
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="ui-page-title text-gradient">
              Dashboard Overview
            </h1>
            <p className="ui-page-subtitle">Welcome back! Here's what's happening with your store today.</p>
          </div>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={onNotificationClick}
              className="relative p-3 bg-cornsilk/80 rounded-2xl border border-moss/15 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer"
            >
              <Bell size={20} className="text-moss" />
              {notifications.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 text-white text-[10px] rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-md animate-pulse" style={{ background: 'linear-gradient(135deg, #BC6C25, #606C38)' }}>
                  {notifications.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Widget Tabs */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-2.5">
          {widgets.map(widget => {
            const Icon = widget.icon;
            const isActive = activeWidget === widget.id;
            return (
              <button
                key={widget.id}
                onClick={() => setActiveWidget(widget.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all duration-200 cursor-pointer ${
                  isActive
                    ? 'text-white shadow-glow'
                    : 'bg-cornsilk/80 text-pakistan/70 hover:bg-white border border-moss/15 shadow-sm'
                }`}
                style={isActive ? { background: 'linear-gradient(135deg, #BC6C25, #606C38)' } : undefined}
              >
                <Icon size={14} />
                {widget.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Metric Cards Grid */}
      <div className="grid gap-6 mb-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {/* Revenue Card */}
        <div className="group">
          <div className="ui-card ui-card-pad bg-gradient-to-br from-white to-emerald-50/20 border-emerald-100/30 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md shadow-emerald-500/10">
                <span className="text-lg font-bold">₹</span>
              </div>
              <span className="text-emerald-700 text-xs font-bold bg-emerald-100/60 px-2.5 py-1 rounded-full border border-emerald-200/40">
                +12.5%
              </span>
            </div>
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1">Total Revenue</h3>
            <p className="text-3xl font-extrabold text-slate-800 tracking-tight">₹{stats.totalRevenue.toLocaleString()}</p>
          </div>
        </div>

        {/* Orders Card */}
        <div className="group">
          <div className="ui-card ui-card-pad bg-gradient-to-br from-white to-blue-50/20 border-blue-100/30 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md shadow-blue-500/10">
                <span className="text-lg font-bold">#</span>
              </div>
              <span className="text-blue-700 text-xs font-bold bg-blue-100/60 px-2.5 py-1 rounded-full border border-blue-200/40">
                +8.2%
              </span>
            </div>
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1">Total Orders</h3>
            <p className="text-3xl font-extrabold text-slate-800 tracking-tight">{stats.totalOrders}</p>
          </div>
        </div>

        {/* Active Customers Card */}
        <div className="group">
          <div className="ui-card ui-card-pad bg-gradient-to-br from-white to-indigo-50/20 border-indigo-100/30 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-md shadow-indigo-500/10">
                <span className="text-lg font-bold">👥</span>
              </div>
              <span className="text-indigo-700 text-xs font-bold bg-indigo-100/60 px-2.5 py-1 rounded-full border border-indigo-200/40">
                +15.3%
              </span>
            </div>
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1">Active Customers</h3>
            <p className="text-3xl font-extrabold text-slate-800 tracking-tight">{stats.activeCustomers}</p>
          </div>
        </div>
      </div>

      {/* Warning Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Low Stock Warning */}
        <div className="group">
          <div className="ui-card ui-card-pad bg-gradient-to-br from-white to-rose-50/20 border-rose-100/30 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 text-white shadow-md shadow-rose-500/10">
                <span className="text-base font-bold">⚠️</span>
              </div>
              <span className="text-rose-700 text-xs font-bold bg-rose-100/60 px-2.5 py-1 rounded-full border border-rose-200/40">
                Critical
              </span>
            </div>
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1">Low Stock Alert</h3>
            <p className="text-3xl font-extrabold text-rose-600 tracking-tight">{stats.lowStock}</p>
            <p className="text-slate-500 text-xs font-semibold mt-1">Products require restocking soon.</p>
          </div>
        </div>

        {/* Expiring Soon Warning */}
        <div className="group">
          <div className="ui-card ui-card-pad bg-gradient-to-br from-white to-amber-50/20 border-amber-100/30 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-md shadow-amber-500/10">
                <span className="text-base font-bold">📅</span>
              </div>
              <span className="text-amber-700 text-xs font-bold bg-amber-100/60 px-2.5 py-1 rounded-full border border-amber-200/40">
                30 Days
              </span>
            </div>
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-1">Expiring Soon</h3>
            <p className="text-3xl font-extrabold text-amber-600 tracking-tight">{stats.expiringProducts}</p>
            <p className="text-slate-500 text-xs font-semibold mt-1">Products expire within a month.</p>
          </div>
        </div>
      </div>

      {activeWidget === 'overview' && (
        <div className="grid gap-8 grid-cols-1 lg:grid-cols-2">
          {/* Recent Orders Overview */}
          <div className="ui-card ui-card-pad shadow-sm">
            <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
              Recent Orders
            </h3>
            <div className="space-y-3 max-h-80 overflow-y-auto pr-1 ui-scrollbar">
              {recentOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <ShoppingCart size={28} className="text-slate-300 mb-2" />
                  <p className="text-sm font-bold text-slate-700">No Orders Yet</p>
                  <p className="text-xs text-slate-500 mt-1">Orders will appear here as customers buy.</p>
                </div>
              ) : recentOrders.slice(0, 5).map(order => {
                const isCompleted = order.status === 'completed' || order.status === 'Delivered';
                const isProcessing = order.status === 'processing' || order.status === 'Shipped';
                return (
                  <div key={order.id} className="flex justify-between items-center p-4 bg-slate-50 hover:bg-slate-100/60 border border-slate-100 rounded-2xl transition-all duration-200 group">
                    <div>
                      <p className="font-bold text-slate-800 text-sm group-hover:text-emerald-600 transition-colors">{order.id}</p>
                      <p className="text-xs text-slate-500 mt-0.5 font-medium">{order.customer || order.customerName}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-extrabold text-slate-800 text-sm">₹{order.total}</p>
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 mt-1 rounded-full ${
                        isCompleted ? 'bg-emerald-50 text-emerald-700 border border-emerald-100/60' :
                        isProcessing ? 'bg-indigo-50 text-indigo-700 border border-indigo-100/60' :
                        'bg-amber-50 text-amber-700 border border-amber-100/60'
                      }`}>
                        {order.status || 'Pending'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Critical Products Overview */}
          <div className="ui-card ui-card-pad shadow-sm">
            <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
              Critical Products
            </h3>
            <div className="space-y-3 max-h-80 overflow-y-auto pr-1 ui-scrollbar">
              {criticalProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200 h-full">
                  <Package size={28} className="text-emerald-500 mb-2" />
                  <p className="text-sm font-bold text-slate-700">All Stocks Good!</p>
                  <p className="text-xs text-slate-500 mt-1">No critical or low stock products in view.</p>
                </div>
              ) : criticalProducts.slice(0, 5).map(product => (
                <div key={product.id} className="flex justify-between items-center p-4 bg-slate-50 hover:bg-slate-100/60 border border-slate-100 rounded-2xl transition-all duration-200">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-slate-200 rounded-xl flex items-center justify-center shadow-sm overflow-hidden shrink-0 border border-slate-300/20">
                      {product.imageUrl ? <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" /> : <span className="text-base">{product.image || '📦'}</span>}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-slate-800 text-sm truncate">{product.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5 font-medium truncate">{product.category}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`text-xs font-bold ${
                      product.stock < 20 ? 'text-rose-600 bg-rose-50 border border-rose-100/40' : 'text-amber-600 bg-amber-50 border border-amber-100/40'
                    } px-2.5 py-1 rounded-lg`}>
                      {product.stock === 0 ? 'Out of Stock' : product.stock < 20 ? `${product.stock} left` : `Expires ${product.expiryDate}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeWidget === 'orders' && (
        <div className="ui-card ui-card-pad shadow-sm">
          <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
            All Recent Orders
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentOrders.length === 0 ? (
               <div className="col-span-full py-12 text-center text-slate-400 font-bold bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                 No orders found.
               </div>
            ) : recentOrders.slice(0, 12).map(order => {
              const isCompleted = order.status === 'completed' || order.status === 'Delivered';
              const isProcessing = order.status === 'processing' || order.status === 'Shipped';
              return (
                <div key={order.id} className="p-5 bg-slate-50 hover:bg-slate-100/60 border border-slate-100 rounded-2xl transition-all duration-200 flex flex-col group">
                  <div className="mb-4">
                    <p className="font-bold text-slate-800 text-sm flex justify-between items-center group-hover:text-emerald-600 transition-colors">
                      {order.id} 
                      <span className="text-[10px] text-slate-400 font-bold bg-white border border-slate-200/60 px-2 py-0.5 rounded-md">{order.date}</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-1.5 font-medium">{order.customer || order.customerName}</p>
                  </div>
                  <div className="text-right mt-auto pt-3 border-t border-slate-200/50 flex justify-between items-center">
                    <span className="text-xs text-slate-400 font-bold">Total</span>
                    <div>
                      <p className="font-extrabold text-slate-800 text-sm">₹{order.total}</p>
                      <span className={`inline-block mt-1.5 text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        isCompleted ? 'bg-emerald-50 text-emerald-700 border border-emerald-100/60' :
                        isProcessing ? 'bg-indigo-50 text-indigo-700 border border-indigo-100/60' :
                        'bg-amber-50 text-amber-700 border border-amber-100/60'
                      }`}>
                        {order.status || 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeWidget === 'products' && (
        <div className="ui-card ui-card-pad shadow-sm">
          <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            Recent Products
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {recentProducts.map(product => (
              <div key={product.id} className="p-5 bg-slate-50 hover:bg-slate-100/60 border border-slate-100 rounded-2xl transition-all duration-250 text-center group">
                <div className="w-16 h-16 mx-auto mb-4 bg-white rounded-2xl flex items-center justify-center shadow-sm overflow-hidden border border-slate-150 group-hover:scale-105 transition-transform duration-200 shrink-0">
                  {product.imageUrl ? <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" /> : <span className="text-2xl">{product.image || '📦'}</span>}
                </div>
                <div>
                  <p className="font-extrabold text-slate-800 text-sm line-clamp-1 group-hover:text-emerald-600 transition-colors">{product.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5 font-bold uppercase tracking-wider">{product.category}</p>
                  <p className="text-xs text-slate-500 mt-3 font-semibold pt-2 border-t border-slate-200/50">
                    Stock: <span className={product.stock === 0 ? 'text-rose-600 font-bold' : product.stock < 20 ? 'text-amber-600 font-bold' : 'text-emerald-600 font-bold'}>{product.stock === 0 ? 'Out of Stock' : product.stock}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeWidget === 'analytics' && (
        <div className="ui-card ui-card-pad shadow-sm">
          <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
            Quick Analytics
          </h3>
          <div className="h-64 flex items-end justify-between gap-4 border-b border-slate-150 pb-4 pr-2 pl-2">
            {monthlyData.map((data, index) => {
              const maxValue = Math.max(...monthlyData.map(d => d.total), 1000); // minimum scale limit
              const heightPct = Math.max((data.total / maxValue) * 100, 5);
              return (
                <div key={index} className="flex flex-col items-center flex-1 h-full" style={{height: '100%'}}>
                  <div className="flex-1 w-full flex items-end justify-center mb-3">
                    <div className="w-12 md:w-16 lg:w-20 bg-gradient-to-t from-emerald-500 to-emerald-600 rounded-t-xl transition-all duration-500 shadow-sm hover:shadow-md hover:from-emerald-600 hover:to-emerald-700 cursor-pointer relative group"
                         style={{ height: `${heightPct}%` }}>
                         <div className="opacity-0 group-hover:opacity-100 absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap z-20">
                           ₹{data.total.toLocaleString()}
                         </div>
                    </div>
                  </div>
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{data.label}</span>
                  <span className="text-[11px] font-extrabold text-slate-700 mt-1">₹{(data.total/1000).toFixed(1)}K</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;